import { useEffect, useSyncExternalStore } from "react";
import { supabase } from "@/lib/supabase";
import { FALLBACK_TAXONOMIES } from "@/lib/taxonomy";

// ক্যাটাগরি আনা + ক্যাশ। ক্যাটাগরি বদলায় কালেভদ্রে আর ডেটা খুবই ছোট (কয়েক KB), তাই:
//   • মেমরি + localStorage-এ ক্যাশ, ঘণ্টায় একবারের বেশি নেটওয়ার্কে যাই না (Supabase free tier-এর egress সাশ্রয়)
//   • একসাথে অনেক কম্পোনেন্ট চাইলেও রিকোয়েস্ট একটাই
//   • ব্যর্থ হলে বা টেবিল না থাকলে (migration 0005 আগে চলেনি) fallback তালিকা — UI কখনো আটকায় না

const CACHE_KEY = "mg.taxonomies.v1";
const TTL_MS = 60 * 60 * 1000;
const SELECT = "key, label_bn, label_en, multi, required, sort, active, terms(value, label_bn, label_en, sort, active)";

function readCache() {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) ?? "null");
    return Array.isArray(c?.data) && c.data.length > 0 && typeof c.at === "number" ? c : null;
  } catch {
    return null;
  }
}

const cached = readCache();
let current = cached?.data ?? FALLBACK_TAXONOMIES;
let fetchedAt = cached?.at ?? 0;
let inflight = null;
const listeners = new Set();

export function normalizeTaxonomies(rows) {
  return [...rows]
    .map((x) => ({ ...x, terms: [...(x.terms ?? [])].sort((a, b) => a.sort - b.sort || a.value.localeCompare(b.value)) }))
    .sort((a, b) => a.sort - b.sort || a.key.localeCompare(b.key));
}

export function refreshTaxonomies({ force = false } = {}) {
  if (!force && Date.now() - fetchedAt < TTL_MS) return Promise.resolve(current);
  inflight ??= (async () => {
    try {
      const { data, error } = await supabase.from("taxonomies").select(SELECT);
      if (error || !Array.isArray(data) || data.length === 0) return current;
      current = normalizeTaxonomies(data);
      fetchedAt = Date.now();
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ at: fetchedAt, data: current }));
      } catch {
        // storage ভরা/বন্ধ — ক্যাশ ছাড়াই চলবে
      }
      listeners.forEach((fn) => fn());
      return current;
    } catch {
      return current;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const getSnapshot = () => current;

export function useTaxonomies() {
  const taxonomies = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    refreshTaxonomies();
  }, []);
  return taxonomies;
}
